import { UserManager } from "oidc-client";
import { authConfig } from "../data/config";
import { authLogger } from "../utils/authLogger";

const userManager = new UserManager(authConfig);

export function signinRedirect() {
  return userManager.signinRedirect();
}

export function signinRedirectCallback() {
  userManager.signinRedirectCallback();
}

// User successfully loaded after login or silent renew
userManager.events.addUserLoaded((user) => {
  authLogger.info("User loaded", {
    expiresAt: new Date(user.expires_at * 1000).toISOString(),
  });
});

// Token is about to expire — silent renew will fire automatically
userManager.events.addAccessTokenExpiring(() => {
  authLogger.info("Access token expiring, attempting silent renew");
});

// Token expired and silent renew did not recover.
// Only redirect if the user is still actively on a page that requires auth.
// Firing signinRedirect() on an idle abandoned session drops them back on
// /contact-info with no state, which can trigger a rate-limit loop.
userManager.events.addAccessTokenExpired(() => {
  authLogger.warn("Access token expired — silent renew did not recover");
  const idlePaths = ["/", "/start", "/signin", "/logout", "/thankyou", "/clock-skew", "/error", "/network", "/recent", "/auto-renew"];
  const currentPath = window.location.pathname;
  const isActivePath = !idlePaths.some((p) => currentPath === p || currentPath.startsWith(p));
  if (isActivePath) {
    authLogger.warn("Redirecting to login — user is on active path", { path: currentPath });
    userManager.signinRedirect();
  } else {
    authLogger.warn("Token expired on idle/terminal path — not redirecting", { path: currentPath });
    userManager.removeUser();
  }
});

// Silent renew attempted and failed
userManager.events.addSilentRenewError((error) => {
  authLogger.error("Silent renew error", {
    error: error.error,
    message: error.message,
    localTime: new Date().toISOString(),
  });

  if (error.message && error.message.includes("iat is in the future")) {
    // Clock skew on the user's device — show a friendly explanation
    authLogger.warn("Silent renew failed due to clock skew on user device");
    authLogger.flush(error);
    window.location.href = "/clock-skew";
    return;
  }

  // Auth server explicitly requires interaction — flush history and re-login
  if (
    error.error === "login_required" ||
    error.error === "interaction_required"
  ) {
    authLogger.flush(error);
    userManager.signinRedirect();
    return;
  }

  // Frame window timed out — the hidden iframe took too long to load
  // (slow network, CDN hiccup, or IdentityServer taking too long).
  // Retry once silently before falling back to a full redirect.
  if (error.message && error.message.includes("Frame window timed out")) {
    authLogger.warn("Silent renew timed out — retrying once");
    userManager.signinSilent().then((user) => {
      authLogger.info("Silent renew retry succeeded", {
        expiresAt: new Date(user.expires_at * 1000).toISOString(),
      });
    }).catch((retryError) => {
      authLogger.error("Silent renew retry also failed", {
        message: retryError.message,
      });
      authLogger.flush(retryError);
      userManager.signinRedirect();
    });
    return;
  }

  // Transient network error — log it but do not redirect.
  // automaticSilentRenew will retry on the next expiry cycle.
  authLogger.flush(error);
});

// User signed out at the auth server (e.g. from another app in the SSO session)
userManager.events.addUserSignedOut(() => {
  authLogger.warn("User signed out at auth server");
  userManager.signinRedirect();
});

// export function signinRedirectCallback() {
//   return userManager.signinRedirectCallback();
// }

export function signoutRedirect() {
  userManager.clearStaleState();
  userManager.removeUser();
  return userManager.signoutRedirect();
}

export function signoutRedirectCallback() {
  userManager.clearStaleState();
  userManager.removeUser();
  return userManager.signoutRedirectCallback();
}

export default userManager;
